import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Truck, ArrowRight, MapPin, Phone, Car, Pencil, Check, X, Navigation, LogOut, Route, ChevronDown, Moon, Sun, ExternalLink, Bell, BellOff, Lock, WifiOff, RefreshCw, CalendarClock, Camera, Calculator } from "lucide-react";
import { toast } from "sonner";
import { api, listTrucks, listJobs, updateJobStatus, updateJob, driverConfirmedClose, getOncallStatus, fetchJobPartnerLink, markJobPartnerOpened, fetchVapidPublicKey, subscribePush, mutePushDevice, ackAaCancelOnRoute } from "../lib/api";
import { enqueueStatus, getQueuedStatusByJob, purgeByJobId, pendingCount, subscribe as subscribeOfflineQueue, flush as flushOfflineQueue, startAutoFlush, isNetworkError } from "../lib/offlineQueue";
import { EmptyState } from "../components/EmptyState";
import { useAuth } from "../lib/auth";
import { useJobArrivalChime } from "../lib/useJobArrivalChime";
import { useNavigate } from "react-router-dom";
import { STATUS_LABEL, SERVICE_LABEL, STATUS_FLOW } from "../lib/constants";
import { formatDistance, formatDuration, cn } from "../lib/utils";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import AvailableJobsPanel from "../components/AvailableJobsPanel";
import OfflineDataBanner from "../components/OfflineDataBanner";
import PushTestButton from "../components/PushTestButton";
import AtScenePhotosModal from "../components/AtScenePhotosModal";
import DriverPriceCheckDialog from "../components/DriverPriceCheckDialog";
import { useDriverPhoneGps } from "../lib/useDriverPhoneGps";
import { tenantLocale, tenantTz } from "../lib/company";

const TRUCK_KEY = "driver_truck_id";
const THEME_KEY = "dispatch_theme";

const mapsLink = (addr, lat, lng) => {
  if (typeof lat === "number" && typeof lng === "number" && (lat !== 0 || lng !== 0)) {
    return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
  }
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(addr || "")}`;
};

function TruckPicker({ trucks, onPick }) {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <div className="inline-flex items-center justify-center h-14 w-14 rounded-xl bg-blue-600 mb-4">
            <Truck className="h-7 w-7 text-white" strokeWidth={2.5} />
          </div>
          <h1 className="font-display text-2xl font-black tracking-tight">Which truck are you?</h1>
          <p className="text-sm text-slate-400 mt-1">Pick your truck to see only the jobs assigned to you.</p>
        </div>
        <div className="space-y-2" data-testid="truck-picker-list">
          {trucks.map((t) => (
            <button
              key={t.id}
              onClick={() => onPick(t)}
              className="w-full text-left bg-slate-900 hover:bg-slate-800 border border-slate-800 hover:border-blue-600 rounded-lg p-4 transition-colors group"
              data-testid={`truck-pick-${t.id}`}
            >
              <div className="flex items-center gap-3">
                <div className={cn(
                  "h-2.5 w-2.5 rounded-full shrink-0",
                  t.status === "available" ? "bg-emerald-500" :
                  t.status === "busy" ? "bg-amber-500" : "bg-slate-500"
                )} />
                <div className="flex-1">
                  <div className="font-display font-bold text-base">{t.name}</div>
                  <div className="text-xs text-slate-400 mt-0.5">{t.driver || "—"} · {t.capacity || "Standard Tow"}</div>
                </div>
                <ArrowRight className="h-4 w-4 text-slate-500 group-hover:text-blue-400 transition-colors" />
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function NotesDialog({ open, job, onOpenChange, onSaved }) {
  const [notes, setNotes] = useState(job?.notes || "");
  const [saving, setSaving] = useState(false);
  const [lastId, setLastId] = useState(job?.id);
  if (job && job.id !== lastId) {
    setLastId(job.id);
    setNotes(job.notes || "");
  }
  const save = async () => {
    if (!job) return;
    setSaving(true);
    try {
      await updateJob(job.id, { notes });
      toast.success("Notes saved");
      onSaved?.();
      onOpenChange(false);
    } catch {
      toast.error("Failed to save notes");
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="driver-notes-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-black">Add Notes</DialogTitle>
          <DialogDescription>{job?.customer_name} · {job?.address}</DialogDescription>
        </DialogHeader>
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={5}
          placeholder="Anything dispatch should know — vehicle condition, customer info, road hazards..."
          data-testid="driver-notes-input"
        />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white">
            {saving ? "Saving..." : "Save Notes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CancelDialog() { return null; }  // legacy stub — kept to avoid touching prop wiring; the trigger button was removed per UX request (drivers ring dispatch instead).

// Provider-specific button labels so the driver sees the same wording they're
// used to in the source portal. Non-source jobs (manual entries) keep the
// generic "Mark X" style.
// Driver-facing action labels per job source. Keyed by the NEXT status the
// button will move the job to. We aim for consistent wording across sources
// so the driver builds muscle memory regardless of where the job came from.
//
// Aug 2026 flow refit: a single big "Next" button per source replaces the
// staggered "Start Travel" + "Start Work" pair. The button label swaps
// depending on the target state — "Accept Job" first, then partner-app
// entry, then the close.
const AA_NEXT_LABEL = {
  accepted: "Accept Job",
  en_route: "Open AA app",
  on_site: "Take Arrival Photos",
  awaiting_partner_close: "Open AA app",
  // `completed` intentionally omitted — the driver never advances a job out
  // of `awaiting_partner_close` themselves; the AA poll auto-closes it once
  // the driver finishes the paperwork inside AA.
};
const FR_NEXT_LABEL = {
  accepted: "Accept Job",
  en_route: "Open First Rescue",
  on_site: "Take Arrival Photos",
  awaiting_partner_close: "Continue to First Rescue →",
  // Same as AA — no manual advance out of awaiting_partner_close.
};
const MANUAL_NEXT_LABEL = {
  accepted: "Accept Job",
  en_route: "Start Navigation",
  on_site: "Take Arrival Photos",
  completed: "Close Job",
};

function nextActionLabel(job, next) {
  if (job?.source === "aa_workforms" || job?.source === "aa_sms") {
    return AA_NEXT_LABEL[next.value] || `Mark ${next.label}`;
  }
  if (job?.source === "servicenow_api" || job?.source === "servicenow_image") {
    return FR_NEXT_LABEL[next.value] || `Mark ${next.label}`;
  }
  return MANUAL_NEXT_LABEL[next.value] || `Mark ${next.label}`;
}

// Live NZ date/time strip for the driver's dash-mounted screen.
function LiveClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return (
    <div
      className="mb-4 flex items-center justify-center gap-2 rounded-md bg-slate-900 dark:bg-slate-800 text-slate-100 px-4 py-2.5"
      data-testid="driver-live-clock"
    >
      <CalendarClock className="h-4 w-4 text-emerald-400 shrink-0" />
      <span className="font-mono text-sm font-bold tabular-nums tracking-wide">
        {now.toLocaleDateString(tenantLocale(), { weekday: "short", day: "numeric", month: "short", year: "numeric", timeZone: tenantTz() })}
        {" · "}
        {now.toLocaleTimeString(tenantLocale(), { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false, timeZone: tenantTz() })}
      </span>
    </div>
  );
}

// Feb 2026 — Photo-count safety net (layers A + B).
// Two touchpoints where we soft-warn a driver about to finalise a job
// without any at-scene photos:
//   A) local "Close Job" (`advance → completed`) — manual/impound jobs
//   B) tapping "Open in AA/FR" while status is on_site or
//      awaiting_partner_close — the last moment before the driver leaves
//      our app to close on the partner side
// Exclusions (both layers): service types where damage photos aren't
// relevant (jumpstart/tire/fuel), impound-source jobs (the billing user runs a
// different photo workflow on those), and cancelled-on-route jobs
// (driver was recalled, no work happened).
function shouldWarnZeroPhotos(job) {
  if (!job) return false;
  const photoCount = (job.photos || []).length;
  if (photoCount > 0) return false;
  const svc = job.service_type;
  if (svc === "jumpstart" || svc === "tire" || svc === "fuel") return false;
  if (job.source === "impound") return false;
  if (job.cancelled_on_route === true) return false;
  return true;
}

function confirmZeroPhotosClose() {
  return window.confirm(
    "You haven't taken any at-scene photos for this job.\n\n" +
    "Insurers need photos to process claims. Close anyway?",
  );
}

function confirmZeroPhotosPartnerOpen() {
  return window.confirm(
    "You haven't taken any at-scene photos for this job yet.\n\n" +
    "You're about to close this job in the partner app. Once it's " +
    "closed there we can't add photos later. Continue anyway?",
  );
}

function JobActionCard({ job, onAdvance, onCancel, onEditNotes, onDriverClose, onOpenPhotos, blockedBy }) {
  // Partner deep-link metadata.
  const isSnSource = job.source === "servicenow_api" || job.source === "servicenow_image";
  const isAaSource = job.source === "aa_workforms" || job.source === "aa_sms";
  const hasPartner = isSnSource || isAaSource;

  // Compute next status. Manual jobs skip `awaiting_partner_close` and go
  // straight from on_site to completed, since there's no partner app to
  // hand off to. Partner jobs follow the full wizard.
  const flowIdx = STATUS_FLOW.findIndex((s) => s.value === job.status);
  let next = flowIdx >= 0 && flowIdx < STATUS_FLOW.length - 1 ? STATUS_FLOW[flowIdx + 1] : null;
  if (next && next.value === "awaiting_partner_close" && !hasPartner) {
    next = STATUS_FLOW[flowIdx + 2] || null;
  }

  const awaitingPartner = job.status === "awaiting_partner_close";

  /** Opens the partner system.
   *
   *  FR (Jun 2026): BARE APP LAUNCH via SCHEME — opens the installed
   *  First Rescue (ServiceNow Agent) app to its home screen. No job
   *  deep-link, no sys_id, no write-back. Backend returns:
   *    - android_intent: "intent://#Intent;scheme=<scheme>;end"
   *    - ios_url:        "<scheme>://"
   *  We launch by scheme (not a bare `package=` intent) because a
   *  package-only intent falls back to the Play Store when Android can't
   *  resolve a launch activity. Scheme is editable in Settings →
   *  ServiceNow card. History: /app/memory/FR_DEEPLINK_HISTORY.md.
   *
   *  AA (all platforms): backend `path` via auto-login page, opened in a
   *  SEPARATE TAB so TowHook keeps running underneath — the driver swipes
   *  back instead of reloading the app. Until Oct 2026 this was a same-tab
   *  `window.location.href`, which remounted /driver on every return and
   *  cost us the arrival-photo bugs documented further down this file.
   */
  const openInPartnerApp = async () => {
    // Opened synchronously on the tap below, before any await — a
    // window.open() that happens after an await is no longer attributed to
    // the tap and mobile browsers block it as a popup. The handle is
    // pointed at the real URL once the link resolves. Null means the
    // browser blocked it anyway (or we're in an iOS standalone PWA), and
    // we fall back to the old same-tab navigation so the driver always
    // gets somewhere.
    let tab = null;
    try {
      if (
        (job.status === "on_site" || job.status === "awaiting_partner_close")
        && shouldWarnZeroPhotos(job)
        && !confirmZeroPhotosPartnerOpen()
      ) {
        return;
      }
      // First Rescue leaves the browser entirely (app scheme) — no tab.
      const isAaJob = job.source === "aa_workforms" || job.source === "aa_sms";
      if (isAaJob) {
        tab = window.open("", "_blank");
        if (tab) {
          // `noopener` would make window.open return null and cost us the
          // handle, so sever the link by hand instead.
          try { tab.opener = null; } catch { /* cross-origin, ignore */ }
          try {
            tab.document.write(
              "<title>Opening AA Workforms…</title>"
              + "<body style='font:16px system-ui;padding:2rem;color:#334155'>"
              + "Opening AA Workforms…</body>",
            );
          } catch { /* some browsers disallow writing, harmless */ }
        }
      }
      markJobPartnerOpened(job.id).catch(() => {});
      const res = await fetchJobPartnerLink(job.id);
      if (!res.ok) {
        tab?.close();
        toast.error("No partner link available for this job");
        return;
      }

      const ua = navigator.userAgent || "";
      const isAndroid = /android/i.test(ua);
      const isIOS = /iphone|ipad|ipod/i.test(ua)
        || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

      // FR — bare launch of the installed app, opened directly.
      if (res.provider === "servicenow") {
        tab?.close();
        if (isAndroid && res.android_intent) { window.location.href = res.android_intent; return; }
        if (isIOS && res.ios_url) { window.location.href = res.ios_url; return; }
        toast.info("Open the First Rescue app on your phone");
        return;
      }

      // AA jobs — auto-login page via path, or plain URL.
      let target = res.url;
      if (!target && res.path) {
        const token = typeof window !== "undefined" ? localStorage.getItem("rescue_token") : null;
        target = `${process.env.REACT_APP_BACKEND_URL}${res.path}${token ? `?t=${token}` : ""}`;
      }
      if (!target) {
        tab?.close();
        toast.error("Couldn't build partner link");
        return;
      }
      if (tab) {
        tab.location.replace(target);
        tab.focus?.();
      } else {
        window.location.href = target;
      }
    } catch {
      tab?.close();
      toast.error("Couldn't open partner app");
    }
  };

  return (
    <div
      className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden"
      data-testid={`driver-job-${job.id}`}
      data-driver-active-job={["accepted", "en_route", "on_site", "awaiting_partner_close"].includes(job.status) ? "true" : "false"}
    >
      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
              {job.queue_position && job.queue_position > 1 && (
                <span
                  className="mr-2 inline-flex items-center text-[10px] font-black tracking-wider bg-blue-600 text-white px-1.5 py-0.5 rounded"
                  data-testid={`driver-queue-pos-${job.id}`}
                >
                  NEXT UP · #{job.queue_position}
                </span>
              )}
              {STATUS_LABEL[job.status]} · {SERVICE_LABEL[job.service_type]}
              {job.aa_job_id && (
                <span
                  className="ml-2 inline-flex items-center text-[10px] font-black tracking-wider bg-amber-200 text-amber-900 px-1.5 py-0.5 rounded"
                  data-testid={`driver-job-aa-id-${job.id}`}
                >
                  AA · {job.aa_job_id}
                </span>
              )}
              {job.work_order_number && (
                <span
                  className="ml-2 inline-flex items-center text-[10px] font-black tracking-wider bg-violet-200 text-violet-900 px-1.5 py-0.5 rounded"
                  data-testid={`driver-job-wo-${job.id}`}
                >
                  FR · {job.work_order_number}
                </span>
              )}
            </div>
            <div className="font-display font-black text-lg leading-tight mt-1">
              {job.customer_name}
            </div>
          </div>
          {job.priority === "urgent" && (
            <span className="text-[10px] font-bold uppercase tracking-wider bg-rose-100 text-rose-800 px-2 py-1 rounded">
              Urgent
            </span>
          )}
        </div>

        <div className="space-y-1.5 text-sm text-slate-700">
          {job.customer_phone && (
            <a href={`tel:${job.customer_phone.replace(/[^+\d]/g, "")}`}
               className="flex items-center gap-2 text-blue-700 hover:underline"
               data-testid={`driver-job-call-${job.id}`}>
              <Phone className="h-4 w-4" /> {job.customer_phone}
            </a>
          )}
          {job.vehicle && (
            <div className="flex items-center gap-2 text-slate-700">
              <Car className="h-4 w-4 text-slate-500" /> {job.vehicle} {job.rego && <span className="font-mono text-xs">· {job.rego}</span>}
            </div>
          )}
          <a
            href={mapsLink(job.address, job.lat, job.lng)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-start gap-2 text-blue-700 hover:underline"
            data-testid={`driver-job-pickup-${job.id}`}
          >
            <MapPin className="h-4 w-4 mt-0.5 text-amber-500 shrink-0" />
            <span className="flex-1">
              <span className="block text-[10px] uppercase font-bold text-slate-500">Pickup</span>
              {job.address}
            </span>
            <Navigation className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          </a>
          {job.drop_off_address && (
            <a
              href={mapsLink(job.drop_off_address, job.drop_off_lat, job.drop_off_lng)}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-start gap-2 text-blue-700 hover:underline"
              data-testid={`driver-job-dropoff-${job.id}`}
            >
              <MapPin className="h-4 w-4 mt-0.5 text-emerald-500 shrink-0" />
              <span className="flex-1">
                <span className="block text-[10px] uppercase font-bold text-slate-500">Drop-off</span>
                {job.drop_off_address}
              </span>
              <Navigation className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            </a>
          )}
          {job.route_duration_s != null && (
            <div className="flex items-center gap-1.5 text-xs text-slate-600 pl-6">
              <Route className="h-3 w-3 text-blue-600" />
              <span className="font-semibold text-blue-700">{formatDuration(job.route_duration_s)}</span>
              <span className="text-slate-400">· {formatDistance(job.route_distance_m)}</span>
            </div>
          )}
          {job.notes && (
            <div className="text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded px-2.5 py-1.5 mt-2">
              <strong>Notes:</strong> {job.notes}
            </div>
          )}
        </div>
      </div>

      {/* Feb 2026 refit — for AA/FR jobs the poller (30s cadence) closes the
          job the moment the driver closes it in the partner app. No manual
          "CLOSE" button on Tow-Hook side. This banner is now purely
          informational so the driver knows the auto-close is inflight. */}
      {awaitingPartner && (
        <div className="border-t border-fuchsia-200 dark:border-fuchsia-800 bg-fuchsia-50 dark:bg-fuchsia-950/30 px-4 py-3" data-testid={`driver-awaiting-banner-${job.id}`}>
          <div className="text-xs font-bold uppercase tracking-wider text-fuchsia-700 dark:text-fuchsia-300 mb-1">
            Waiting for {isSnSource ? "First Rescue" : "AA"} to close…
          </div>
          <div className="text-sm text-fuchsia-900 dark:text-fuchsia-100">
            When you close this job in {isSnSource ? "First Rescue" : "AA"}, it will disappear from your screen automatically (usually within 30 seconds).
          </div>
        </div>
      )}

      <div className="border-t border-slate-100 dark:border-slate-800 p-2 flex flex-wrap gap-2 bg-slate-50 dark:bg-slate-900">
        {/* Feb 2026 — Persistent "TAKE PHOTOS" button. Always visible
            while the driver is actively working the job (assigned →
            awaiting_partner_close). Opens the SAME modal that the
            geofence auto-pops at scene, so the driver can:
              • Re-open the popup if they dismissed the auto-arrival one
              • Take photos before/during/after scene when the geofence
                misses (poor GPS, indoor sites, short tows)
              • Add extra photos at any point during the job
            Full-width and bold-amber so it's "in their face" without
            competing with the primary action button (slate/emerald).
            Photo count on the label = driver's own progress indicator. */}
        {["assigned", "accepted", "en_route", "on_site", "awaiting_partner_close"].includes(job.status) && (
          <Button
            onClick={() => onOpenPhotos?.(job)}
            className="w-full h-12 text-base font-black uppercase tracking-wide bg-amber-500 hover:bg-amber-600 text-white border border-amber-600 shadow-md shadow-amber-600/30 dark:bg-amber-600 dark:hover:bg-amber-500 dark:border-amber-500"
            data-testid={`driver-take-photos-${job.id}`}
          >
            <Camera className="h-5 w-5" />
            Take Photos
            {(job.photos?.length || 0) > 0 && (
              <span className="ml-1.5 inline-flex items-center rounded-full bg-white/25 px-2 py-0.5 text-xs font-black">
                {job.photos.length} taken
              </span>
            )}
          </Button>
        )}
        {/* Feb 2026 refit — AA/FR jobs: single "Open in <Partner>" button.
            No local Accept/En Route/On Site/Close taps. The poller (30s)
            plus telematics geo-fence advance status silently. Photo prompt
            still triggers via the auto-arrival hook when status → on_site.
            Only shown in-progress statuses; hidden once poller confirms
            completed/cancelled. */}
        {hasPartner && !["completed", "cancelled"].includes(job.status) && (
          <>
            <div
              className="w-full flex items-center justify-between px-1 pb-1.5 text-[10px] uppercase tracking-wider font-bold"
              data-testid={`driver-partner-status-${job.id}`}
            >
              <span className={cn(
                "inline-flex items-center gap-1.5 px-2 py-0.5 rounded",
                isSnSource
                  ? "bg-violet-100 text-violet-800 dark:bg-violet-950/40 dark:text-violet-200"
                  : "bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200"
              )}>
                <RefreshCw className="h-3 w-3" />
                Syncing from {isSnSource ? "First Rescue" : "AA"} · {STATUS_LABEL[job.status]}
              </span>
            </div>
            {blockedBy ? (() => {
              const blockerIsPartner = blockedBy.source === "aa_workforms" || blockedBy.source === "aa_sms"
                || blockedBy.source === "servicenow_api" || blockedBy.source === "servicenow_image";
              const blockerPartnerName = (blockedBy.source === "servicenow_api" || blockedBy.source === "servicenow_image")
                ? "First Rescue"
                : "AA";
              const blockerLabel = blockedBy.rego || blockedBy.customer_name || "your current job";
              const tooltip = blockerIsPartner
                ? `Finish ${blockerLabel} in ${blockerPartnerName} first — this app will catch up automatically within 30 seconds.`
                : `Finish ${blockerLabel} first — tap Close Job on it before starting this one.`;
              return (
                <Button
                  size="sm"
                  disabled
                  variant="outline"
                  className="flex-1 min-w-[140px] font-semibold cursor-not-allowed opacity-70 border-dashed"
                  title={tooltip}
                  data-testid={`driver-blocked-${job.id}`}
                >
                  <Lock className="h-3.5 w-3.5 mr-1.5" />
                  Finish {blockedBy.rego || "current job"} first
                </Button>
              );
            })() : (
              <Button
                size="sm"
                onClick={openInPartnerApp}
                className="flex-1 min-w-[140px] font-semibold bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white"
                data-testid={`driver-open-partner-${job.id}`}
              >
                <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                Open in {isSnSource ? "First Rescue" : "AA"}
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={() => onEditNotes(job)}
              data-testid={`driver-notes-btn-${job.id}`}
            >
              <Pencil className="h-3.5 w-3.5 mr-1" /> Notes
            </Button>
          </>
        )}

        {/* Manual / impound jobs — the original staged advance flow.
            No partner poller behind these, so the driver drives status. */}
        {!hasPartner && (
          <>
        {/* "One job at a time" lock: when the truck already has a different
            job actively in progress, replace the advance button with a
            greyed-out lock pill naming the blocking job. Wording depends on
            the blocker's source — partner-app jobs auto-close from the AA/FR
            poll, manual jobs need Close Job tapped here. */}
        {blockedBy && next && job.status !== "completed" && job.status !== "cancelled" && (() => {
          const blockerIsPartner = blockedBy.source === "aa_workforms" || blockedBy.source === "aa_sms"
            || blockedBy.source === "servicenow_api" || blockedBy.source === "servicenow_image";
          const blockerPartnerName = (blockedBy.source === "servicenow_api" || blockedBy.source === "servicenow_image")
            ? "First Rescue"
            : "AA";
          const blockerLabel = blockedBy.rego || blockedBy.customer_name || "your current job";
          const tooltip = blockerIsPartner
            ? `Finish ${blockerLabel} in ${blockerPartnerName} first — this app will catch up automatically within a minute.`
            : `Finish ${blockerLabel} first — tap Close Job on it before starting this one.`;
          return (
            <Button
              size="sm"
              disabled
              variant="outline"
              className="flex-1 min-w-[140px] font-semibold cursor-not-allowed opacity-70 border-dashed"
              title={tooltip}
              data-testid={`driver-blocked-${job.id}`}
            >
              <Lock className="h-3.5 w-3.5 mr-1.5" />
              Finish {blockedBy.rego || "current job"} first
            </Button>
          );
        })()}
        {!blockedBy && next && !awaitingPartner && job.status !== "completed" && job.status !== "cancelled" && (
          <Button
            size="sm"
            onClick={() => {
              // Aug 2026 refit: when advancing from `accepted` → `en_route`,
              // ALSO launch the driver into the right place — partner app
              // for AA/FR, Google Maps for manual/impound. One tap, no
              // fiddling.
              if (next.value === "en_route" && hasPartner) {
                openInPartnerApp();
              } else if (next.value === "en_route" && !hasPartner) {
                // Manual/impound → open turn-by-turn to the pickup.
                if (job.address || (job.lat && job.lng)) {
                  const q = job.lat && job.lng
                    ? `${job.lat},${job.lng}`
                    : encodeURIComponent(job.address);
                  window.open(`https://www.google.com/maps/dir/?api=1&destination=${q}`, "_blank", "noopener");
                }
              }
              // Original behaviour: when advancing INTO awaiting_partner_close
              // (i.e. driver returning to hand off after on-scene work),
              // re-open the partner app for the paperwork.
              if (next.value === "awaiting_partner_close" && hasPartner) {
                openInPartnerApp();
              }
              onAdvance(job, next.value);
            }}
            className="flex-1 min-w-[140px] font-semibold bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white"
            data-testid={`driver-advance-${job.id}`}
          >
            <Check className="h-3.5 w-3.5 mr-1.5" /> {nextActionLabel(job, next)}
          </Button>
        )}
        {/* Feb 2026 — Yard-outbound / manual escape hatch: a job that
            somehow ended up at `awaiting_partner_close` without a partner
            source (yard_outbound, manual) has no partner ticket to wait
            for. Give the driver a plain Close Job button so they aren't
            trapped. Backend collapses to completed on the write. */}
        {!blockedBy && !hasPartner && awaitingPartner && (
          <Button
            size="sm"
            onClick={() => onAdvance(job, "completed")}
            className="flex-1 min-w-[140px] font-semibold bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid={`driver-escape-partner-close-${job.id}`}
            title="This job doesn't need a partner-app close — tap to finish."
          >
            <Check className="h-3.5 w-3.5 mr-1.5" /> Close Job
          </Button>
        )}
        {/* "Re-open partner app" block removed Feb 2026 — awaitingPartner
            can never be true inside the !hasPartner branch (partner-app
            close status only occurs on AA/FR jobs, all handled above). */}
        <Button
          size="sm"
          variant="outline"
          onClick={() => onEditNotes(job)}
          data-testid={`driver-notes-btn-${job.id}`}
        >
          <Pencil className="h-3.5 w-3.5 mr-1" /> Notes
        </Button>
        {/* "Cancelled on Route" intentionally driver-side removed (June 2026):
            it confused drivers — they ring dispatch, who records it with the
            Charge / No Charge choice on the job card. */}
        {/* "Can't Complete" intentionally removed — drivers ring dispatch
            if a job isn't achievable, so we don't expose a self-cancel path. */}
          </>
        )}
      </div>
    </div>
  );
}

export default function DriverPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [trucks, setTrucks] = useState([]);
  const [pickedTruckId, setPickedTruckId] = useState(() => localStorage.getItem(TRUCK_KEY) || "");
  // Push-notification permission state: "granted" | "denied" | "default" |
  // "unsupported". Drives the header CTA — without "granted" the device
  // never gets background job alerts.
  const [notifStatus, setNotifStatus] = useState(() =>
    (typeof Notification === "undefined" ? "unsupported" : Notification.permission));
  // Per-device push mute — the driver's own off-switch so their personal
  // phone doesn't ding all night when someone else is on call.
  const [pushMuted, setPushMuted] = useState(() => localStorage.getItem("nts_push_muted") === "1");
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notesFor, setNotesFor] = useState(null);
  const [cancelFor, setCancelFor] = useState(null);
  const [isOnCall, setIsOnCall] = useState(false);
  const [oncallPrompt, setOncallPrompt] = useState(null); // legacy — unused since Feb 2026 (popup removed); state kept only so `set` is a no-op if any stale caller references it.
  const [atScenePhotosFor, setAtScenePhotosFor] = useState(null);
  // Driver-side "Cancelled On-Route" acknowledge modal target. Set by the
  // loadJobs poll when a job the driver is committed to lands with
  // charge_decision_needed=true. Tapping Acknowledge unassigns the truck
  // (backend) so the driver rolls to the next queued job while the
  // dispatcher decides Charge / No Charge at their pace.
  const [cancelOnRouteAckFor, setCancelOnRouteAckFor] = useState(null);
  const [ackSaving, setAckSaving] = useState(false);
  // Price Check dialog — only shown to on-call drivers (after-hours).
  const [priceCheckOpen, setPriceCheckOpen] = useState(false);
  // Offline queue — count of photos/status updates saved on this phone
  // waiting for signal. Drives the amber sync banner.
  const [pendingSync, setPendingSync] = useState(0);
  const prevPendingRef = useRef(0);
  const pendingSinceRef = useRef(0);
  const wasOfflineRef = useRef(false);
  // Feb 2026 — Phone GPS reporter. No-op unless the tenant switches
  // gps_provider to "phone". `phoneGpsStatus` powers the tiny indicator
  // next to the driver name so drivers can spot permission problems.
  const [phoneGpsStatus, setPhoneGpsStatus] = useState(null);
  useDriverPhoneGps({ enabled: true, onStatus: setPhoneGpsStatus });
  // Driver default = dark mode (trucks at night). Toggle still works either way.
  const [theme, setTheme] = useState(
    () => localStorage.getItem(THEME_KEY) || "dark"
  );

  // Apply theme to <html> on mount + when toggled. (Shared key with dispatcher topbar.)
  // CRITICAL: see Topbar.jsx for the full reason — must set BOTH `data-theme`
  // (CSS variables) AND `.dark` class (Tailwind utilities). Setting only one
  // silently breaks half the dark-mode styling.
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute("data-theme", theme);
    root.classList.toggle("dark", theme === "dark");
    localStorage.setItem(THEME_KEY, theme);
  }, [theme]);

  const pickedTruck = useMemo(
    () => trucks.find((t) => t.id === pickedTruckId),
    [trucks, pickedTruckId]
  );

  const loadTrucks = useCallback(async () => {
    try {
      const data = await listTrucks();
      setTrucks(data);
    } catch {
      toast.error("Failed to load trucks");
    }
  }, []);

  // Counter for consecutive loadJobs failures. `loadJobs` polls every 10s;
  // a single transient network blip should never bother the driver with a
  // toast. Only surface an error after 3 consecutive failures (~30s of
  // sustained trouble), and never on the initial "no jobs" success path.
  const jobsFailureCount = useRef(0);
  // Auto-arrival bookkeeping: last-seen status per job + which arrivals we
  // already popped the photo modal for (avoid re-opening after dismissal).
  //
  // Aug 2026 — `handledArrivalsRef` is now MIRRORED to localStorage so a
  // tab remount (any of: driver taps "Open in AA" which does a same-tab
  // window.location.href navigation and hits back; iOS Safari memory-
  // pressure evicting the PWA tab; iOS returning from the native camera
  // reloading the tab) doesn't lose which jobs the driver has already
  // dealt with. Without this the photo modal would either fail to open
  // at all (for partner jobs the `cameFromNonOnSite` guard was already
  // failing on remount — see the drop below) or spuriously re-open after
  // the driver had finished photos.
  //
  // The server-side `job.photos.length >= 4` check below is the PRIMARY
  // dedupe — this ref is a defensive fast-path only. `photos_skip_reason`
  // continues to cover the emergency-skip / dark-skip cases.
  const prevStatusesRef = useRef({});
  // Feb 2026 (A63608597) — track which AA-deallocated jobs we've already
  // toasted so remount/re-poll doesn't spam. Session-scoped by design;
  // if the driver refreshes we're fine to re-notify once.
  const deallocToastedRef = useRef(new Set());
  const HANDLED_ARRIVALS_KEY = "nts_handled_arrivals_v1";
  const handledArrivalsRef = useRef(new Set(
    (() => {
      try {
        const raw = localStorage.getItem(HANDLED_ARRIVALS_KEY);
        return raw ? JSON.parse(raw) : [];
      } catch { return []; }
    })()
  ));
  const persistHandledArrivals = () => {
    try {
      localStorage.setItem(
        HANDLED_ARRIVALS_KEY,
        JSON.stringify([...handledArrivalsRef.current]),
      );
    } catch { /* private-mode / quota — ref still works in-memory */ }
  };

  // In-app chime for job arrivals (restored Aug 2026 after being removed in
  // July's cleanup — drivers reported "no notification sound" during
  // business hours because web push alone was too quiet on many phones).
  // Escalating: 1 ding for the 1st job, 2 for the 2nd, etc — driver hears
  // their queue backing up. Auto-unlocks on any user gesture (picking a
  // truck is enough), so no "Enable Alerts" button clutter is needed.
  const { unlock: unlockChime } = useJobArrivalChime(jobs.length);

  // Web push: subscribe this device to job alerts for the picked truck.
  // Push fires even when the app is closed (system notification sound).
  const subscribeToPush = useCallback(async (truckId) => {
    try {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
      if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
      const reg = await navigator.serviceWorker.register("/sw.js");
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        const key = await fetchVapidPublicKey();
        const raw = atob(key.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(key.length / 4) * 4, "="));
        const appKey = new Uint8Array([...raw].map((ch) => ch.charCodeAt(0)));
        sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: appKey });
      }
      await subscribePush(sub.toJSON(), truckId, localStorage.getItem("nts_push_muted") === "1");
    } catch (e) {
      console.warn("Push subscribe failed (non-fatal):", e);
    }
  }, []);

  useEffect(() => {
    if (!pickedTruckId || notifStatus !== "granted") return;
    subscribeToPush(pickedTruckId);
  }, [pickedTruckId, notifStatus, subscribeToPush]);

  // Ask the browser for notification permission. MUST be called from a user
  // gesture (tap) or Chrome/Safari silently ignore it — we hook it to
  // picking a truck and to the Enable Alerts / Enable Notifications buttons.
  const requestNotifPermission = useCallback(async () => {
    if (typeof Notification === "undefined") return;
    if (Notification.permission === "denied") {
      setNotifStatus("denied");
      toast.error(
        "Notifications are blocked for this app on this device. Enable them in your device settings (Site/App settings → Notifications → Allow), then reopen the app.",
        { duration: 9000 },
      );
      return;
    }
    try {
      const res = await Notification.requestPermission();
      setNotifStatus(res);
      if (res === "granted") {
        toast.success("Job notifications enabled — you'll get alerts even when the app is closed.");
        if (pickedTruckId) subscribeToPush(pickedTruckId);
      } else if (res === "denied") {
        toast.warning("Notifications declined — you'll only hear job alerts while the app is open on screen.", { duration: 7000 });
      }
    } catch {
      /* very old browsers use the callback form — non-fatal */
    }
  }, [pickedTruckId, subscribeToPush]);

  // Toggle push mute for THIS device only. Local flag flips instantly (works
  // offline); the server-side flag syncs best-effort so the backend actually
  // stops sending to this phone.
  const togglePushMute = useCallback(async () => {
    const next = !pushMuted;
    setPushMuted(next);
    localStorage.setItem("nts_push_muted", next ? "1" : "0");
    try {
      const reg = await navigator.serviceWorker.getRegistration("/sw.js");
      const sub = reg && (await reg.pushManager.getSubscription());
      if (sub) await mutePushDevice(sub.endpoint, next);
    } catch {
      /* offline / not yet subscribed — flag re-syncs on the next subscribe */
    }
    if (next) {
      toast.warning("Job notifications muted on this phone. You won't get alerts here until you turn them back on.", { duration: 6000 });
    } else {
      toast.success("Job notifications back on for this phone.");
    }
  }, [pushMuted]);

  const loadJobs = useCallback(async () => {
    if (!pickedTruckId) {
      setJobs([]);
      setLoading(false);
      jobsFailureCount.current = 0;
      return;
    }
    try {
      const data = await listJobs({ truck_id: pickedTruckId });
      // Overlay any offline-queued status changes so a poll refresh doesn't
      // visually revert a job the driver already advanced while off-grid.
      let queued = null;
      try { queued = await getQueuedStatusByJob(); } catch { /* IDB unavailable */ }
      // Feb 2026 (A63608597) — resurrection guard on the overlay. If the
      // server reports a job as terminal but IndexedDB still holds a stale
      // en_route/on_site queued status, applying it would re-mark the job
      // active and pin it to the driver's queue forever. Detect those,
      // skip the overlay AND purge the stale queue item on the spot so it
      // doesn't retry-loop against the (now-guarded) backend. Also toast
      // the driver ONCE per session per job when AA/dispatcher yanks it.
      const withLocal = (data || []).map((j) => {
        const serverTerminal = ["completed", "cancelled"].includes(j.status);
        if (queued && queued.has(j.id) && !serverTerminal) {
          return { ...j, ...queued.get(j.id) };
        }
        if (serverTerminal && queued && queued.has(j.id)) {
          // Fire-and-forget cleanup — don't await, we don't want IDB
          // slowness to block the render.
          purgeByJobId(j.id).catch(() => { /* best effort */ });
        }
        return j;
      });
      // One-shot "AA cancelled this on you" toast for jobs the poller
      // just yanked. We track already-toasted ids in a ref so a
      // remount/re-poll doesn't spam. Only fires for jobs THIS truck
      // was assigned to and where the server explicitly stamped
      // `dealloc_notify_at`.
      //
      // Charge-decision jobs (Feb 2026 AA cancel-on-route flow) go via
      // the modal branch below INSTEAD of a silent toast — driver must
      // acknowledge to free their queue slot. We skip the toast path
      // for those so we don't double-notify.
      for (const j of data || []) {
        if (j.assigned_truck_id === pickedTruckId
            && j.dealloc_notify_at
            && !j.charge_decision_needed
            && !deallocToastedRef.current.has(j.id)) {
          deallocToastedRef.current.add(j.id);
          const label = j.rego || j.customer_name || j.aa_job_id || "A job";
          toast.warning(`${label} was cancelled by AA — removed from your queue.`, { duration: 8000 });
          // Also purge any offline queue items for it in case the
          // driver was mid-advance when AA yanked it.
          purgeByJobId(j.id).catch(() => { /* best effort */ });
        }
      }
      // AA cancel-on-route driver modal (Feb 2026 SaaS refit): when the
      // AA cancel-email detector or poller vanish-branch flags a job the
      // driver is committed to, we surface a MUST-TAP modal here instead
      // of a passive toast. Acknowledging frees the driver's queue slot
      // (backend clears assigned_truck_id) so they can move to the next
      // job while the dispatcher decides Charge / No Charge at their pace.
      if (!cancelOnRouteAckFor) {
        const needAck = (data || []).find((j) =>
          j.assigned_truck_id === pickedTruckId
          && j.charge_decision_needed
          && !j.driver_acked_cancel_at
        );
        if (needAck) setCancelOnRouteAckFor(needAck);
      }
      // Driver only sees jobs that are still actionable for THIS truck.
      // Completed / cancelled jobs stay in MongoDB and remain visible to the dispatcher,
      // but they no longer clutter the driver's mobile queue.
      const active = withLocal.filter(j =>
        j.assigned_truck_id === pickedTruckId &&
        !["completed", "cancelled"].includes(j.status)
      );
      // Order: the driver's currently-active job (accepted / en_route / on_site /
      // awaiting_partner_close) MUST always pin to the top so a fresh "Now"
      // dispatch never preempts the job the driver is physically working on.
      // Below the active job, sort by queue_position, then created_at fallback.
      const statusRank = {
        awaiting_partner_close: -4,
        on_site: -3,
        en_route: -2,
        accepted: -1,
        assigned: 1,
        pending: 2,
      };
      active.sort((a, b) => {
        const ra = statusRank[a.status] ?? 9;
        const rb = statusRank[b.status] ?? 9;
        if (ra !== rb) return ra - rb;
        const qa = a.queue_position ?? 999;
        const qb = b.queue_position ?? 999;
        if (qa !== qb) return qa - qb;
        return (a.created_at || "").localeCompare(b.created_at || "");
      });
      setJobs((prev) => {
        // Detect an actual reorder / addition (Aug 2026 kiosk-peek toast).
        // Ignore the first-load case + status-only changes on the same set.
        const prevIds = prev.map((j) => j.id);
        const nextIds = active.map((j) => j.id);
        const isFirstLoad = prev.length === 0;
        const sameSet =
          prevIds.length === nextIds.length &&
          prevIds.every((id) => nextIds.includes(id));
        const changedOrder = sameSet &&
          prevIds.some((id, idx) => id !== nextIds[idx]);
        const grew = nextIds.length > prevIds.length && !isFirstLoad;
        if ((changedOrder || grew) && !isFirstLoad) {
          toast.info("Queue updated", { duration: 2500 });
        }
        return active;
      });
      // GPS auto-arrival (server set an AA/FR job On Scene while we were
      // en route) → pop the photo prompt exactly as if the driver had tapped
      // "At Scene" themselves.
      //
      // Aug 2026 — the partner-branch `cameFromNonOnSite` guard used to
      // require the previous poll to have observed a non-on_site status.
      // That guard silently killed the modal any time the DriverPage
      // remounted after status had already flipped to on_site (which
      // happens on EVERY tap of "Open in AA / First Rescue" because that
      // does a same-tab `window.location.href` navigation — hitting back
      // remounts /driver with an empty `prevStatuses`). The server-side
      // `job.photos` array is now the source of truth for "photos
      // already dealt with"; combined with `photos_skip_reason` and the
      // localStorage-persisted `handledArrivalsRef`, we can safely open
      // the modal whenever an on_site partner job hasn't yet been
      // photographed, no matter how many times the tab has been reloaded.
      //
      // Manual/impound jobs keep the `en_route` guard because they still
      // have a manual "Take Arrival Photos" button on the card — the
      // auto-arrival hook is a bonus for them, not the only path.
      const prevStatuses = prevStatusesRef.current;
      for (const j of active) {
        const isPartnerJob =
          j.source === "aa_workforms" || j.source === "aa_sms" ||
          j.source === "servicenow_api" || j.source === "servicenow_image";
        const photosAlreadyTaken = Array.isArray(j.photos) && j.photos.length >= 4;
        const arrivalGate = isPartnerJob
          // Partner jobs — no manual button, so auto-arrival IS the only
          // trigger. Fire once per job (persistent-ref + server-photo-count
          // both prevent re-firing).
          ? true
          // Manual/impound — only auto-fire when we saw en_route on the
          // previous poll (backfill for a driver who left the tab open).
          : (prevStatuses[j.id] === "en_route");
        if (
          j.status === "on_site" && j.auto_arrival_at &&
          !j.photos_skip_reason && !photosAlreadyTaken &&
          arrivalGate && !handledArrivalsRef.current.has(j.id)
        ) {
          handledArrivalsRef.current.add(j.id);
          persistHandledArrivals();
          setAtScenePhotosFor((cur) => cur || j);
        }
      }
      prevStatusesRef.current = Object.fromEntries(active.map((j) => [j.id, j.status]));
      jobsFailureCount.current = 0;
    } catch (err) {
      // Silent retry policy — driver runs on flakey mobile data; a single blip
      // must never toast. Only surface after 3 consecutive failures — and even
      // then keep it low-key. Feb 2026: was previously a red "check signal"
      // error which looked like an app crash; softened to an amber refresh
      // notice so drivers don't lose faith in the app on a bad-coverage day.
      jobsFailureCount.current += 1;
      if (jobsFailureCount.current === 3) {
        toast.info("Refreshing job list, please wait…");
      }
      // Diagnostic aid for driver-side flakiness — visible in DevTools.
      console.warn("loadJobs failed (attempt", jobsFailureCount.current, "):", err);
    } finally {
      setLoading(false);
    }
  }, [pickedTruckId]);

  // initial trucks load
  useEffect(() => { loadTrucks(); }, []);

  // jobs load + 10s refresh
  useEffect(() => {
    loadJobs();
    if (!pickedTruckId) return;
    const t = setInterval(loadJobs, 10000);
    return () => clearInterval(t);
  }, [pickedTruckId, loadJobs]);

  // Feb 2026 — when the driver returns to our tab after opening the FR
  // or AA partner app, `visibilitychange` fires with document.hidden=false.
  // Trigger an immediate refresh so the queue is fresh (e.g. the job may
  // have been auto-arrived while the partner app was foregrounded) —
  // otherwise the driver waits up to 10 s for the next poll cycle. Only
  // fires on transitions FROM hidden, so it's cheap.
  useEffect(() => {
    const onVisible = () => {
      if (!document.hidden && pickedTruckId) loadJobs();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [pickedTruckId, loadJobs]);

  // Offline queue lifecycle — auto-flush on `online` event / 20s timer,
  // badge count subscription, and a "synced" toast + refresh when the
  // queue fully drains after a no-signal stretch.
  useEffect(() => {
    startAutoFlush();
    pendingCount().then((n) => { prevPendingRef.current = n; setPendingSync(n); }).catch(() => {});
    const unsub = subscribeOfflineQueue((n) => {
      if (prevPendingRef.current > 0 && n === 0) {
        // Oct 2026 — every photo goes through the queue now, even on full
        // signal, so this fired after each ordinary upload and told the
        // driver they'd been offline when they never were. It also landed
        // over the camera's close button. Only say it when the queue
        // genuinely waited: either the phone dropped out, or the backlog
        // took longer than a normal background upload.
        const stalled = Date.now() - (pendingSinceRef.current || Date.now()) > 8000;
        if (wasOfflineRef.current || stalled) {
          toast.success("Back online — offline photos & updates synced.");
        }
        wasOfflineRef.current = false;
        pendingSinceRef.current = 0;
        loadJobs();
      }
      if (prevPendingRef.current === 0 && n > 0) pendingSinceRef.current = Date.now();
      if (n > 0 && typeof navigator !== "undefined" && !navigator.onLine) {
        wasOfflineRef.current = true;
      }
      prevPendingRef.current = n;
      setPendingSync(n);
    });
    flushOfflineQueue();
    return unsub;
  }, [loadJobs]);

  // Return-from-partner-app polish. When the driver taps Navigate (Google/Apple
  // Maps) or "Open in AA/SN app", we lose focus. When they finish there and
  // switch back to our PWA, we want to (a) refresh their jobs immediately —
  // the AA/SN status may have changed while they were away — and (b) scroll
  // the currently-active job (en-route / on-site) into view so they don't
  // have to hunt through the queue. This is the closest we can get to
  // "deep-link back to the driver page" without partner-side support.
  useEffect(() => {
    if (!pickedTruckId) return;
    let awayAt = 0;
    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        awayAt = Date.now();
        return;
      }
      // Only trigger when we've been away for >5 s (ignores tab flicker).
      if (awayAt && Date.now() - awayAt > 5000) {
        awayAt = 0;
        loadJobs();
        // Give the DOM a beat to re-render the fresh list, then scroll to
        // the active job (en-route beats on-site beats assigned).
        setTimeout(() => {
          const el =
            document.querySelector('[data-driver-active-job="true"]') ||
            document.querySelector('[data-testid^="driver-job-"]');
          if (el && "scrollIntoView" in el) {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }, 400);
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [pickedTruckId, loadJobs]);

  // Poll on-call status every 30s so the Available Jobs panel auto-appears
  // when after-hours kicks in (or when the office flips the manual override).
  // One-time purge of legacy oncall_prompt_* flags (Aug 2026 regression
  // fix). Older builds cached "already prompted" in localStorage without
  // clearing it on logout, so drivers who dismissed the prompt weeks ago
  // never got asked again. Removing them all on mount = every fresh page
  // load will re-ask if we're currently in an on-call window.
  useEffect(() => {
    Object.keys(localStorage).forEach((k) => {
      if (k.startsWith("oncall_prompt_")) localStorage.removeItem(k);
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const s = await getOncallStatus();
        if (!cancelled) setIsOnCall(!!s.is_on_call);
      } catch { /* non-fatal */ }
    };
    tick();
    const t = setInterval(tick, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const onPickTruck = (truck) => {
    localStorage.setItem(TRUCK_KEY, truck.id);
    setPickedTruckId(truck.id);
    // Picking a truck is a user gesture — the one moment the browser lets
    // us pop the "Allow notifications?" prompt AND unlock the AudioContext
    // for the in-app chime (browser autoplay policy).
    unlockChime();
    if (notifStatus === "default") requestNotifPermission();
  };

  // (Feb 2026) The "Are you on-call tonight?" popup was removed. Drivers no
  // longer need to self-identify — after-hours, any driver who logs in can
  // see and claim any pending job (first-come-first-served). The topbar
  // "Afterhours" badge still signals to dispatch when the system is in
  // self-serve mode. See ROADMAP.md for the multi-truck onboarding
  // rationale.

  const switchTruck = () => {
    localStorage.removeItem(TRUCK_KEY);
    // Clear any legacy oncall_prompt_* flags so switching to a new truck
    // always re-asks "Are you on-call?" (Aug 2026 regression fix).
    Object.keys(localStorage).forEach((k) => {
      if (k.startsWith("oncall_prompt_")) localStorage.removeItem(k);
    });
    setPickedTruckId("");
    setJobs([]);
  };

  const advance = async (job, status) => {
    // Intercept "On Site / At Scene" — driver MUST take damage photos (or pick
    // an emergency skip reason) before we advance the status.
    // Exception: jumpstarts, tyre changes and fuel delivery have no damage to
    // record, so we skip the photo modal entirely. Lockouts get the modal but
    // photos are optional (no damage to record unless the driver chooses to document).
    if (status === "on_site") {
      const svc = job.service_type;
      if (svc === "jumpstart" || svc === "tire" || svc === "fuel") {
        // No-op skip — straight through to On Site.
      } else {
        setAtScenePhotosFor(job);
        return;
      }
    }
    // Aug 2026 refit: MANUAL/IMPOUND close routes through the new endpoint
    // that writes a real per-tap `completed_at` + `auto_closed_source =
    // "driver_confirmed"` — protects the timestamp from the poller mirror's
    // shared-`now_iso` collapse. AA/FR closes use the same endpoint, but from
    // the dedicated "I've finished in AA — CLOSE" button (see driverClose).
    if (status === "completed") {
      // Layer A — soft photo-count warning before local close.
      if (shouldWarnZeroPhotos(job) && !confirmZeroPhotosClose()) return;
      try {
        await driverConfirmedClose(job.id);
        toast.success("Job closed");
        loadJobs();
      } catch (e) {
        if (isNetworkError(e)) {
          try {
            await enqueueStatus(job.id, "completed");
            setJobs((cur) => cur
              .map((j) => (j.id === job.id ? { ...j, status: "completed" } : j))
              .filter((j) => !["completed", "cancelled"].includes(j.status)));
            toast.warning("No signal — close saved on this phone. It'll sync automatically.", { duration: 5000 });
          } catch {
            toast.error("No signal and couldn't save the close — try again when you have coverage.");
          }
          return;
        }
        const detail = e?.response?.data?.detail;
        toast.error(detail || "Failed to close job");
      }
      return;
    }
    try {
      await updateJobStatus(job.id, status);
      toast.success(`Marked ${STATUS_LABEL[status]}`);
      loadJobs();
    } catch (e) {
      // No signal — save the transition on the phone and advance the UI
      // optimistically. Syncs automatically when coverage returns.
      if (isNetworkError(e)) {
        try {
          await enqueueStatus(job.id, status);
          setJobs((cur) => cur
            .map((j) => (j.id === job.id ? { ...j, status } : j))
            .filter((j) => !["completed", "cancelled"].includes(j.status)));
          toast.warning("No signal — update saved on this phone. It'll sync automatically.", { duration: 5000 });
        } catch {
          toast.error("No signal and couldn't save the update — try again when you have coverage.");
        }
        return;
      }
      // Surface the "one job at a time" lock (409) with its full message
      // so the driver sees the name of the blocking job; fall back to a
      // generic toast for other failures.
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail) {
        toast.error(detail, { duration: 6000 });
      } else {
        toast.error(detail || "Failed to update status");
      }
    }
  };

  const onAtSceneContinue = async (skipReason) => {
    const job = atScenePhotosFor;
    if (!job) return;
    // Feb 2026 — the modal is opened from two places now:
    //   (a) geofence auto-pop when the driver arrives (status = en_route
    //       transitioning to on_site — this call is what makes that jump)
    //   (b) the persistent "Take Photos" button, from ANY active status
    //       (accepted / en_route / on_site / awaiting_partner_close)
    // Only fire the status advance to on_site when we're in case (a) —
    // i.e. the driver is currently en_route. Otherwise a driver taking
    // photos at their yard or mid-tow would silently mark themselves
    // "at scene" from the wrong place, misfiring metrics + partner
    // status pushes.
    if (job.status !== "en_route") {
      try {
        if (skipReason) await updateJob(job.id, { photos_skip_reason: skipReason });
      } catch { /* non-fatal */ }
      setAtScenePhotosFor(null);
      loadJobs();
      return;
    }
    try {
      await updateJobStatus(job.id, "on_site", skipReason || null);
      toast.success("Marked At Scene");
      setAtScenePhotosFor(null);
      loadJobs();
    } catch (e) {
      // No signal — queue the At Scene transition behind any offline photos
      // already saved by the modal, advance the UI, and let the driver work.
      if (isNetworkError(e)) {
        try {
          await enqueueStatus(job.id, "on_site", skipReason || null);
          setJobs((cur) => cur.map((j) =>
            j.id === job.id
              ? { ...j, status: "on_site", ...(skipReason ? { photos_skip_reason: skipReason } : {}) }
              : j
          ));
          setAtScenePhotosFor(null);
          toast.warning("No signal — photos & status saved on this phone. They'll sync automatically.", { duration: 6000 });
        } catch {
          toast.error("No signal and couldn't save the update — try again when you have coverage.");
        }
        return;
      }
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail) {
        toast.error(detail, { duration: 6000 });
      } else {
        toast.error(detail || "Failed to update status");
      }
    }
  };

  const cancelWithReason = async (reason) => {
    if (!cancelFor) return;
    try {
      // Append the reason as notes before flipping to cancelled
      const newNotes = `${cancelFor.notes ? cancelFor.notes + "\n" : ""}[Driver cancel] ${reason}`;
      await updateJob(cancelFor.id, { notes: newNotes });
      await updateJobStatus(cancelFor.id, "cancelled");
      toast.success("Job marked as cannot complete");
      setCancelFor(null);
      loadJobs();
    } catch {
      toast.error("Failed to cancel job");
    }
  };

  const onLogout = () => {
    localStorage.removeItem(TRUCK_KEY);
    // Clear any legacy on-call prompt suppression flags stashed by earlier
    // builds — otherwise phones that dismissed the prompt weeks ago never
    // get asked again (Aug 2026 regression fix).
    Object.keys(localStorage).forEach((k) => {
      if (k.startsWith("oncall_prompt_")) localStorage.removeItem(k);
    });
    logout();
    navigate("/login", { replace: true });
  };

  if (!pickedTruckId) {
    return trucks.length > 0
      ? <TruckPicker trucks={trucks} onPick={onPickTruck} />
      : (
        <div className="min-h-screen bg-slate-950 text-slate-300 flex items-center justify-center">
          {loading ? "Loading trucks…" : "No trucks set up — ask dispatch."}
        </div>
      );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-slate-950 text-white sticky top-0 z-10 shadow-md" data-testid="driver-header">
        <div className="px-3 sm:px-5 pt-3 sm:pt-4 pb-2 flex items-center gap-2 sm:gap-3">
          <Truck className="h-8 w-8 sm:h-9 sm:w-9 text-blue-400 shrink-0" strokeWidth={2.5} />
          <div className="flex items-center gap-2 sm:gap-3 flex-1">
          <button
            onClick={() => setTheme((t) => (t === "dark" ? "light" : "dark"))}
            className="flex-1 justify-center p-2 sm:p-2.5 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-100 transition-colors inline-flex items-center"
            title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            data-testid="driver-theme-toggle"
            aria-label="Toggle theme"
          >
            {theme === "dark" ? <Sun className="h-4 w-4 text-amber-400" /> : <Moon className="h-4 w-4" />}
          </button>
          {/* Price Check — available to every driver. Lets any driver quote
              a round-trip tow on the spot without bugging dispatch. The
              `isOnCall`-only gate was removed Feb 2026 after on-call
              self-identification was retired (jobs are now FCFS across all
              drivers), so pinning this to the on-call driver stopped making
              sense. */}
          <button
            onClick={() => setPriceCheckOpen(true)}
            className="flex-1 justify-center p-2 sm:p-2.5 rounded-md bg-emerald-700 hover:bg-emerald-600 text-white transition-colors inline-flex items-center gap-1.5"
            title="Quick tow price check"
            data-testid="driver-price-check-btn"
            aria-label="Price check"
          >
            <Calculator className="h-4 w-4" />
            <span className="hidden sm:inline text-sm font-semibold">Price</span>
          </button>
          {/* Feb 2026 — phone-GPS permission banner. Only shows when the
              tenant is on phone-GPS mode AND the browser has actually
              refused location access. Silent in every other case
              (telematics_guru, manual, permission granted). */}
          {phoneGpsStatus?.state === "permission-denied-or-error" && (
            <div
              className="text-sm font-semibold px-3 py-2 sm:py-2.5 rounded-md bg-rose-700 text-white inline-flex items-center justify-center gap-2 flex-1"
              title="Enable location access in your browser/device settings so dispatch can see where your truck is"
              data-testid="driver-phone-gps-blocked"
            >
              <MapPin className="h-4 w-4" />
              <span className="hidden sm:inline">Location blocked</span>
            </div>
          )}
          {/* Job-alert controls: push permission + per-device mute. The old
              in-app chime (green bell) was removed July 2026 — push
              notifications now cover all job-alert sounds. */}
          {(notifStatus === "default" || notifStatus === "denied") && (
            <button
              onClick={requestNotifPermission}
              className={`text-sm font-semibold px-2.5 sm:px-4 py-2 sm:py-2.5 rounded-md text-white inline-flex items-center justify-center gap-1.5 sm:gap-2 transition-colors flex-1 ${
                notifStatus === "denied"
                  ? "bg-rose-700 hover:bg-rose-600"
                  : "bg-amber-600 hover:bg-amber-500 animate-pulse"
              }`}
              data-testid="driver-enable-notifications-btn"
              title={notifStatus === "denied"
                ? "Notifications are blocked in your device settings — tap for instructions"
                : "Tap to get job alerts even when the app is closed"}
              aria-label={notifStatus === "denied" ? "Notifications blocked" : "Enable notifications"}
            >
              <BellOff className="h-4 w-4" />
              <span className="hidden sm:inline">
                {notifStatus === "denied" ? "Notifications Blocked" : "Enable Notifications"}
              </span>
            </button>
          )}
          {/* Per-device mute — lets a driver silence their personal phone
              overnight without touching device settings. Only shown once
              push permission is granted. */}
          {notifStatus === "granted" && (
            <button
              onClick={togglePushMute}
              className={`text-sm font-semibold px-2.5 sm:px-4 py-2 sm:py-2.5 rounded-md inline-flex items-center justify-center gap-1.5 sm:gap-2 transition-colors flex-1 ${
                pushMuted
                  ? "bg-amber-600 hover:bg-amber-500 text-white"
                  : "bg-slate-800 hover:bg-slate-700 text-slate-100"
              }`}
              data-testid="driver-push-mute-btn"
              title={pushMuted
                ? "Job notifications are MUTED on this phone — tap to turn them back on"
                : "Job notifications are on for this phone — tap to mute (e.g. overnight when you're not on call)"}
              aria-label={pushMuted ? "Notifications muted — tap to unmute" : "Mute notifications"}
            >
              {pushMuted ? (
                <>
                  <BellOff className="h-4 w-4" />
                  <span className="hidden sm:inline">Muted</span>
                </>
              ) : <Bell className="h-4 w-4 text-sky-400" />}
            </button>
          )}
          {notifStatus === "granted" && (
            <PushTestButton ensureSubscribed={() => subscribeToPush(pickedTruckId)} />
          )}
          <button
            onClick={onLogout}
            className="text-sm font-semibold px-2.5 sm:px-4 py-2 sm:py-2.5 rounded-md bg-slate-800 hover:bg-rose-700 text-slate-100 inline-flex items-center justify-center gap-1.5 sm:gap-2 transition-colors flex-1"
            data-testid="driver-logout-btn"
            aria-label="Sign out"
          >
            <LogOut className="h-4 w-4" />
            <span className="hidden sm:inline">Sign Out</span>
          </button>
          </div>
        </div>
        <div className="px-3 sm:px-5 pb-3 sm:pb-4">
          <div
            className="w-full rounded-md bg-slate-900/80 border border-slate-800 px-4 py-2.5 flex items-center gap-3"
            data-testid="driver-truck-identity"
          >
            <div className="min-w-0 flex-1">
              <div className="font-display font-black text-lg sm:text-2xl leading-tight truncate">
                {pickedTruck?.name || "Truck"}
              </div>
              <div className="text-xs sm:text-sm text-slate-400 truncate mt-0.5">
                {user?.name} · {pickedTruck?.driver || "Driver"}
              </div>
            </div>
            <button
              onClick={switchTruck}
              className="shrink-0 inline-flex items-center gap-2 px-4 py-3 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-100 font-semibold transition-colors"
              data-testid="driver-switch-truck-btn"
              aria-label="Switch truck"
              title="Switch truck"
            >
              <ChevronDown className="h-6 w-6" />
              <span className="hidden sm:inline text-sm">Switch Truck</span>
            </button>
          </div>
        </div>
      </header>

      <main className="p-4 max-w-3xl mx-auto" data-testid="driver-main">
        <LiveClock />
        <OfflineDataBanner />
        {pendingSync > 0 && (
          <div
            className="mb-4 px-4 py-3 rounded-md bg-amber-100 dark:bg-amber-950/40 border border-amber-300 dark:border-amber-700 flex items-center gap-3"
            data-testid="driver-offline-sync-banner"
          >
            <WifiOff className="h-5 w-5 text-amber-700 dark:text-amber-300 shrink-0" />
            <div className="flex-1 text-sm font-semibold text-amber-800 dark:text-amber-200">
              {pendingSync} update{pendingSync === 1 ? "" : "s"} saved on this phone — will sync automatically when signal returns.
            </div>
            <button
              onClick={() => flushOfflineQueue()}
              className="text-xs font-bold px-3 py-1.5 rounded bg-amber-600 hover:bg-amber-500 text-white inline-flex items-center gap-1 shrink-0"
              data-testid="driver-offline-sync-retry-btn"
            >
              <RefreshCw className="h-3 w-3" /> Retry now
            </button>
          </div>
        )}

        <AvailableJobsPanel
          truckId={pickedTruckId}
          truckName={pickedTruck?.name}
          isOnCall={isOnCall}
          onClaimed={loadJobs}
        />

        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display font-black text-2xl tracking-tight">
            Your Jobs <span className="text-slate-400 text-lg font-bold">({jobs.length})</span>
          </h2>
          {jobs.length > 0 && (
            <div className="text-xs uppercase tracking-wider text-slate-500 font-bold">
              Next up at top
            </div>
          )}
        </div>

        {loading && jobs.length === 0 && (
          <div className="text-center text-slate-500 py-12 text-base">Loading your jobs…</div>
        )}

        {!loading && jobs.length === 0 && (
          <EmptyState
            icon={Truck}
            title="No jobs assigned right now"
            hint="Refreshes every 10 seconds. New jobs will pop in automatically as dispatch assigns them."
            testId="driver-empty"
          />
        )}

        <div className="space-y-3">
          {(() => {
            // "One job at a time" gating (Feb 2026). Compute the truck's
            // currently-active job (if any). While one job is accepted /
            // en_route / on_site / awaiting_partner_close, every other card
            // shows a locked pill instead of the advance button. Keeps
            // drivers from stacking up open jobs and forces them to tap
            // Close Job on our side before starting the next.
            const BLOCKING = ["accepted", "en_route", "on_site", "awaiting_partner_close"];
            const activeJob = jobs.find((j) => BLOCKING.includes(j.status));
            // Feb 2026: reverted the Aug 2026 "kiosk + peek" mode. Drivers
            // now see the full queue as normal cards again — active job
            // pins to the top (via statusRank above), everything else
            // sits below it and shows a locked pill until the active job
            // completes. The one-job-at-a-time gate keeps the flow safe.
            return jobs.map((j) => (
              <JobActionCard
                key={j.id}
                job={j}
                onAdvance={advance}
                onCancel={(jj) => setCancelFor(jj)}
                onEditNotes={(jj) => setNotesFor(jj)}
                onDriverClose={(jj) => advance(jj, "completed")}
                onOpenPhotos={(jj) => setAtScenePhotosFor(jj)}
                blockedBy={activeJob && activeJob.id !== j.id ? activeJob : null}
              />
            ));
          })()}
        </div>
      </main>

      <NotesDialog
        open={!!notesFor}
        job={notesFor}
        onOpenChange={(o) => !o && setNotesFor(null)}
        onSaved={loadJobs}
      />

      <CancelDialog
        open={!!cancelFor}
        job={cancelFor}
        onOpenChange={(o) => !o && setCancelFor(null)}
        onConfirm={cancelWithReason}
      />
      <AtScenePhotosModal
        open={!!atScenePhotosFor}
        job={atScenePhotosFor}
        onOpenChange={(o) => {
          if (!o) {
            setAtScenePhotosFor(null);
            // Feb 2026 — refresh the parent's job state on modal
            // close (with or without confirm) so `job.photos` reflects
            // anything the driver uploaded. Without this, the "Take
            // Photos · N taken" badge on the persistent button (and
            // the modal's own re-hydration on next open) would show
            // stale counts.
            loadJobs();
          }
        }}
        onContinue={onAtSceneContinue}
      />

      {/* AA cancel-on-route acknowledge modal (Feb 2026). Full-screen so
          the driver can't miss it. Tap Acknowledge → backend clears
          assigned_truck_id → driver rolls to next queued job. Dispatcher
          still owes a Charge / No Charge decision on the flagged job. */}
      <Dialog
        open={!!cancelOnRouteAckFor}
        onOpenChange={(o) => { if (!o && !ackSaving) setCancelOnRouteAckFor(null); }}
      >
        <DialogContent
          className="max-w-md border-rose-600 border-4"
          data-testid="driver-cancel-on-route-ack-dialog"
        >
          <DialogHeader>
            <div className="mx-auto mb-2 flex h-14 w-14 items-center justify-center rounded-full bg-rose-100 dark:bg-rose-950">
              <X className="h-8 w-8 text-rose-600" strokeWidth={3} />
            </div>
            <DialogTitle className="font-display text-2xl font-black tracking-tight text-center text-rose-700">
              Cancelled On-Route
            </DialogTitle>
            <DialogDescription className="text-center text-base pt-2">
              AA cancelled <strong className="text-slate-900 dark:text-slate-100">
                {cancelOnRouteAckFor?.rego || cancelOnRouteAckFor?.customer_name || cancelOnRouteAckFor?.aa_job_id || "this job"}
              </strong> while you were on the way.
              <span className="block mt-3 text-sm text-slate-600 dark:text-slate-400">
                Tap acknowledge to free this job from your queue so you can move to the next one. Dispatch will handle the charge decision.
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-2">
            <Button
              className="w-full h-12 bg-rose-600 hover:bg-rose-700 text-white font-bold text-base"
              disabled={ackSaving}
              onClick={async () => {
                if (!cancelOnRouteAckFor?.id) return;
                setAckSaving(true);
                try {
                  await ackAaCancelOnRoute(cancelOnRouteAckFor.id);
                  toast.success("Acknowledged — job removed from your queue.");
                  // Purge any offline queue items for it so a stale
                  // en_route flush doesn't try to advance a job we've
                  // ack'd away from.
                  purgeByJobId(cancelOnRouteAckFor.id).catch(() => {});
                  setCancelOnRouteAckFor(null);
                  loadJobs();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Couldn't acknowledge — try again");
                } finally {
                  setAckSaving(false);
                }
              }}
              data-testid="driver-cancel-on-route-ack-btn"
            >
              {ackSaving ? "Acknowledging…" : "Acknowledge — Free My Queue"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* On-call self-identification popup removed (Feb 2026) — after-hours
          jobs are now first-come-first-served across ALL drivers. See the
          topbar "Afterhours" badge for the system-level mode indicator. */}

      <DriverPriceCheckDialog
        open={priceCheckOpen}
        onOpenChange={setPriceCheckOpen}
      />
    </div>
  );
}
